/**
 * Linkage: the referral cards under an answer (Katalog v4, sheet AI Chatbot,
 * bagian G).
 *
 * The design is the one the Navibiz website's own assistant runs on: the
 * model chooses, the list decides. Each answer gets a short list of
 * candidates from the project's referral list, each under a handle (L1, L2,
 * …). The model may end its answer with `[[link:L2|alasan]]`, and the gateway
 * resolves that handle back to the stored link. A handle it invented resolves
 * to nothing, so the chatbot can never hand a visitor a link that is not on
 * the client's list.
 *
 * A referral is any destination on the site, not just a product: a page, a
 * section of a page ("founders" on the About page), a product, an article, a
 * downloadable document. Knowledge entries may name the referral that shows
 * where their fact lives, so an answer about the founders points at the
 * founders section even when the visitor's words do not match its title.
 */
import { z } from "zod";
import type { KnowledgeChunk } from "./knowledge";
import type { ControlMarker } from "./markers";
import { Bm25Index, type Searchable } from "./retrieval";
import type { ChatLink, LinkKind } from "./wire";

export const LINK_KINDS = ["page", "section", "product", "article", "document"] as const satisfies readonly LinkKind[];

export const LINK_KIND_LABELS: Record<LinkKind, string> = {
  page: "Halaman",
  section: "Bagian halaman",
  product: "Produk / layanan",
  article: "Artikel",
  document: "Dokumen",
};

/**
 * Where a referral came from. Sync sources own their links: a feed or API
 * sync switches off what it no longer sends, but never touches links a
 * person added by hand.
 */
export const LINK_SOURCES = ["manual", "import", "sitemap", "feed", "api"] as const;
export type LinkSource = (typeof LINK_SOURCES)[number];

/** Result of the last link check. Only `broken` (twice in a row) switches a link off. */
export const LINK_STATUSES = ["unchecked", "ok", "broken", "section_missing", "error"] as const;
export type LinkStatus = (typeof LINK_STATUSES)[number];

/** Catalog: "paling banyak 3 di atas ambang relevansi". */
export const MAX_LINKS_PER_ANSWER = 3;
/** How many candidates one answer may choose from. More costs tokens and blurs the choice. */
export const LINK_CANDIDATES = 10;
/**
 * When the model named nothing, the gateway offers a referral on its own
 * judgement only above this BM25 score: a plain greeting or an off-topic
 * question stays without cards.
 */
export const LINK_FALLBACK_MIN_SCORE = 2.5;
/** Knowledge matches weaker than this do not pull their referral in. */
const RELATED_MIN_SCORE = 1;
/** Hard ceiling per project; far above any UMKM catalogue, low enough for per-answer ranking. */
export const MAX_LINKS_PER_PROJECT = 1000;
/** External websites: setup includes up to this many referrals, compiled by Navibiz. */
export const SETUP_LINK_ALLOWANCE = 100;

/** A referral as the answering pipeline sees it. */
export interface LinkRef {
  id: string;
  key: string;
  kind: LinkKind;
  title: string;
  summary: string;
  url: string;
  price: string | null;
  imageUrl: string | null;
  tags: string[];
}

const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => value || null);

/** One referral as the Console, imports, feeds, and PUT /v1/links accept it. URLs are resolved separately. */
export const linkInputSchema = z.object({
  key: z.string().trim().min(1).max(200),
  kind: z.enum(LINK_KINDS),
  title: z.string().trim().min(2).max(160),
  url: z.string().trim().min(1).max(1000),
  summary: z.string().trim().max(300).optional().default(""),
  price: nullableText(60),
  imageUrl: nullableText(1000),
  tags: z.array(z.string().trim().min(1).max(40)).max(12).optional().default([]),
  active: z.boolean().optional().default(true),
});
export type LinkDraft = z.infer<typeof linkInputSchema>;

/** What a project allows Linkage to point at, and where its links sync from. */
export const linkageSettingsSchema = z.object({
  /** Hosts referrals may point at: "toko.biz.id", or "*.toko.biz.id" for its subdomains. */
  domains: z.array(z.string().trim().toLowerCase().regex(/^(\*\.)?[a-z0-9.-]+\.[a-z]{2,}$|^localhost$/, "Domain tanpa skema, mis. toko.biz.id")).max(20),
  feed: z
    .object({
      format: z.enum(["navibiz", "shopify", "woocommerce", "csv"]),
      url: z.url({ protocol: /^https?$/ }),
    })
    .nullable(),
});
export type LinkageSettings = z.infer<typeof linkageSettingsSchema>;

// ---- URLs ---------------------------------------------------------------------

/**
 * Resolves a referral URL to an absolute http(s) URL. Relative paths
 * ("/menu/kopi-susu", "#founders") come from template exports and resolve
 * against the website; anything else (mailto:, javascript:) is refused.
 */
export function resolveLinkUrl(raw: string, base: string | null): string | null {
  try {
    const url = base ? new URL(raw.trim(), base) : new URL(raw.trim());
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    if (url.username || url.password) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function hostOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** The domains a new project starts with: its own website's host. */
export function defaultLinkDomains(websiteUrl: string | null): string[] {
  const host = hostOf(websiteUrl);
  return host ? [host] : [];
}

/** True when the URL's host is on the project's list ("*.x" = any subdomain of x, not x itself). */
export function urlAllowed(url: string, domains: readonly string[]): boolean {
  const host = hostOf(url);
  if (!host) return false;
  return domains.some((domain) => (domain.startsWith("*.") ? host.endsWith(domain.slice(1)) : host === domain));
}

/**
 * A link that scrolls to and highlights a piece of text, for sections that
 * have no id (`#:~:text=`). Safari, Chrome and Firefox all honour it; an
 * older browser simply opens the page at the top.
 */
export function textFragmentUrl(pageUrl: string, text: string): string {
  const url = new URL(pageUrl);
  url.hash = "";
  const phrase = text.replace(/\s+/g, " ").trim().slice(0, 80);
  // encodeURIComponent leaves "-" alone, which the fragment syntax reserves.
  return `${url.toString()}#:~:text=${encodeURIComponent(phrase).replace(/-/g, "%2D")}`;
}

/** Same document, ignoring the fragment and a trailing slash: "/tentang" and "/tentang/#tim" are one page. */
export function samePage(a: string, b: string): boolean {
  try {
    const x = new URL(a);
    const y = new URL(b);
    const path = (url: URL) => url.pathname.replace(/\/+$/, "") || "/";
    return x.origin === y.origin && path(x) === path(y) && x.search === y.search;
  } catch {
    return false;
  }
}

/** The words a URL carries: "/menu/kopi-susu-aren#tentang-kami" → "menu kopi susu aren tentang kami". */
function urlWords(url: string): string {
  try {
    const parsed = new URL(url);
    const hash = decodeURIComponent(parsed.hash.replace(/^#(:~:text=)?/, ""));
    return `${decodeURIComponent(parsed.pathname)} ${hash}`.replace(/[/_\-#.]+/g, " ").trim();
  } catch {
    return "";
  }
}

// ---- ranking ------------------------------------------------------------------

interface LinkDoc extends Searchable {
  link: LinkRef;
}

export type LinkIndex = Bm25Index<LinkDoc>;

/** BM25 over the referrals' titles and tags (double weight), summaries, and URL words. */
export function buildLinkIndex(links: readonly LinkRef[]): LinkIndex {
  return new Bm25Index<LinkDoc>(
    links.map((link) => ({
      id: link.id,
      title: link.title,
      tags: link.tags,
      text: [link.summary, LINK_KIND_LABELS[link.kind], urlWords(link.url)].filter(Boolean).join("\n"),
      link,
    })),
  );
}

/** A referral the knowledge points at, with how strongly its entry matched (1 = the best match). */
export interface RelatedLink {
  key: string;
  weight: number;
}

/**
 * Referral keys of the knowledge entries this question leans on, strongest
 * first, each weighted by its entry's match relative to the best one.
 * "Siapa founder-nya?" matches the entry about the founders (weight 1), and
 * that entry knows its section of the About page.
 */
export function relatedLinkKeys(index: Bm25Index<KnowledgeChunk>, questions: { latest: string; previous?: string }): RelatedLink[] {
  const related: RelatedLink[] = [];
  const collect = (hits: { chunk: KnowledgeChunk; score: number }[], damp: number) => {
    const best = hits[0]?.score ?? 0;
    for (const { chunk, score } of hits) {
      if (score >= RELATED_MIN_SCORE && chunk.link && !related.some((item) => item.key === chunk.link)) related.push({ key: chunk.link, weight: (score / best) * damp });
    }
  };
  collect(index.search(questions.latest, 3), 1);
  if (questions.previous) collect(index.search(questions.previous, 1), 0.5);
  return related;
}

export interface LinkCandidate {
  /** "L1", "L2", …: what the model writes. Only meaningful within one answer. */
  handle: string;
  link: LinkRef;
  score: number;
  /** Pulled in by a knowledge entry the answer leans on. */
  related: boolean;
  /** A section of the page the visitor already has open: the widget scrolls instead of navigating. */
  here: boolean;
}

/**
 * The short list one answer may choose from: referrals the knowledge points
 * at first, then the best word matches for this question and, at half
 * weight, the one before it, so "kalau yang ukuran besar?" keeps its subject.
 *
 * The page the visitor is reading is never offered back to them, the rule
 * navibiz-web learned the hard way. A section of that page is the exception:
 * someone on the About page asking about the founders is best served by
 * being scrolled to them.
 */
export function selectLinkCandidates(input: {
  links: readonly LinkRef[];
  index: LinkIndex;
  latest: string;
  previous?: string;
  /** From {@link relatedLinkKeys}; plain keys count as full-strength, in order. */
  relatedKeys?: readonly (RelatedLink | string)[];
  pageUrl?: string;
  limit?: number;
}): LinkCandidate[] {
  const limit = input.limit ?? LINK_CANDIDATES;
  const scores = new Map<string, number>();
  for (const { chunk, score } of input.index.search(input.latest, limit * 2)) scores.set(chunk.id, score);
  if (input.previous) {
    for (const { chunk, score } of input.index.search(input.previous, Math.ceil(limit / 2))) {
      scores.set(chunk.id, (scores.get(chunk.id) ?? 0) + score / 2);
    }
  }

  const byKey = new Map(input.links.map((link) => [link.key, link]));
  const byId = new Map(input.links.map((link) => [link.id, link]));
  const top = Math.max(0, ...scores.values());
  const related = new Set<string>();
  (input.relatedKeys ?? []).forEach((item, i, all) => {
    const { key, weight } = typeof item === "string" ? { key: item, weight: 1 - i / (all.length + 1) } : item;
    const link = byKey.get(key);
    if (!link) return;
    related.add(link.id);
    // The best-matching entry's referral goes above every word match; an entry that only
    // half-matched the question gets half that lift, so a strong direct match can still win.
    scores.set(link.id, (scores.get(link.id) ?? 0) + weight * (top + 1));
  });

  const ranked: LinkCandidate[] = [];
  for (const [id, score] of [...scores.entries()].sort((a, b) => b[1] - a[1])) {
    const link = byId.get(id);
    if (!link) continue;
    const onThisPage = input.pageUrl ? samePage(link.url, input.pageUrl) : false;
    if (onThisPage && !pointsIntoPage(link)) continue;
    ranked.push({ handle: "", link, score: Number(score.toFixed(3)), related: related.has(id), here: onThisPage });
    if (ranked.length >= limit) break;
  }
  return ranked.map((candidate, i) => ({ ...candidate, handle: `L${i + 1}` }));
}

function pointsIntoPage(link: LinkRef): boolean {
  if (link.kind === "section") return true;
  try {
    return new URL(link.url).hash.length > 1;
  } catch {
    return false;
  }
}

/** The candidate list as the model reads it, in the uncached part of the prompt. */
export function renderLinkCandidates(candidates: readonly LinkCandidate[]): string {
  return candidates
    .map(({ handle, link, here }) => {
      const summary = link.summary.length > 110 ? `${link.summary.slice(0, 107).trimEnd()}…` : link.summary;
      return [
        handle,
        LINK_KIND_LABELS[link.kind],
        link.title,
        link.price,
        here ? "ada di halaman yang sedang dibuka" : null,
      ]
        .filter(Boolean)
        .join(" · ")
        .concat(summary ? ` — ${summary}` : "");
    })
    .join("\n");
}

export function toChatLink(link: LinkRef, reason: string): ChatLink {
  return { id: link.id, kind: link.kind, title: link.title, url: link.url, reason: reason || link.summary, price: link.price, imageUrl: link.imageUrl };
}

/**
 * Turns the model's picks into cards. Unknown handles vanish. When the model
 * named nothing, the gateway offers the referral the knowledge points at, or
 * a very strong word match, but never over a deliberate "I don't know": a
 * card under "we don't sell that" reads as a contradiction.
 */
export function resolveLinks(
  markers: readonly ControlMarker[],
  candidates: readonly LinkCandidate[],
  options: { declined: boolean; max?: number },
): { links: ChatLink[]; chosen: string[]; fallback: boolean } {
  const max = options.max ?? MAX_LINKS_PER_ANSWER;
  const byHandle = new Map(candidates.map((candidate) => [candidate.handle, candidate]));
  const links: ChatLink[] = [];
  for (const marker of markers) {
    if (marker.type !== "link" || links.length >= max) continue;
    const candidate = byHandle.get(marker.handle);
    if (candidate && !links.some((link) => link.id === candidate.link.id)) links.push(toChatLink(candidate.link, marker.reason));
  }
  if (links.length > 0 || options.declined) return { links, chosen: links.map((link) => link.id), fallback: false };

  const strong = candidates.filter((candidate) => candidate.related || candidate.score >= LINK_FALLBACK_MIN_SCORE).slice(0, 2);
  const fallback = strong.map((candidate) => toChatLink(candidate.link, ""));
  return { links: fallback, chosen: fallback.map((link) => link.id), fallback: fallback.length > 0 };
}

// ---- CSV ------------------------------------------------------------------------

/** Header names accepted in a referral CSV, Indonesian or English. */
const CSV_COLUMNS: Record<string, keyof LinkDraft> = {
  judul: "title",
  title: "title",
  nama: "title",
  url: "url",
  tautan: "url",
  link: "url",
  ringkasan: "summary",
  deskripsi: "summary",
  summary: "summary",
  description: "summary",
  harga: "price",
  price: "price",
  gambar: "imageUrl",
  image: "imageUrl",
  foto: "imageUrl",
  jenis: "kind",
  kind: "kind",
  tag: "tags",
  tags: "tags",
  kode: "key",
  key: "key",
  id: "key",
};

const KIND_WORDS: Record<string, LinkKind> = {
  halaman: "page",
  page: "page",
  bagian: "section",
  section: "section",
  produk: "product",
  layanan: "product",
  product: "product",
  service: "product",
  artikel: "article",
  blog: "article",
  article: "article",
  dokumen: "document",
  document: "document",
  pdf: "document",
};

/** RFC 4180-ish: quoted fields, doubled quotes, commas or semicolons (Excel in Indonesian locale). */
export function parseCsv(text: string): string[][] {
  const firstLine = text.slice(0, text.indexOf("\n") === -1 ? undefined : text.indexOf("\n"));
  const delimiter = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i]!;
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') quoted = false;
      else field += char;
    } else if (char === '"' && field === "") quoted = true; // a quote opens a field only at its start
    else if (char === delimiter) {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = [];
      field = "";
    } else field += char;
  }
  row.push(field);
  if (row.some((cell) => cell.trim())) rows.push(row);
  return rows;
}

/**
 * A referral sheet (from Google Sheets, Excel, or a shop export) as drafts.
 * Needs at least a title and a URL column; kind defaults to product, key to
 * the URL. Rows that do not validate are reported, not silently dropped.
 */
export function linksFromCsv(text: string): { drafts: LinkDraft[]; errors: string[] } {
  const [header, ...rows] = parseCsv(text.replace(/^﻿/, ""));
  if (!header) return { drafts: [], errors: ["File kosong."] };
  const columns = header.map((name) => CSV_COLUMNS[name.trim().toLowerCase()]);
  if (!columns.includes("title") || !columns.includes("url")) return { drafts: [], errors: ["Kolom wajib: judul dan url."] };
  const drafts: LinkDraft[] = [];
  const errors: string[] = [];
  rows.forEach((cells, i) => {
    const raw: Record<string, string> = {};
    columns.forEach((column, j) => {
      if (column) raw[column] = (cells[j] ?? "").trim();
    });
    const parsed = linkInputSchema.safeParse({
      key: raw.key || raw.url,
      kind: KIND_WORDS[(raw.kind ?? "").toLowerCase()] ?? "product",
      title: raw.title,
      url: raw.url,
      summary: raw.summary,
      price: raw.price,
      imageUrl: raw.imageUrl,
      tags: (raw.tags ?? "").split(/[|,;]/).map((tag) => tag.trim()).filter(Boolean),
    });
    if (parsed.success) drafts.push(parsed.data);
    else errors.push(`Baris ${i + 2}: ${parsed.error.issues[0]?.path.join(".")} ${parsed.error.issues[0]?.message}`);
  });
  return { drafts, errors };
}

/** "Rp 25.000" from a number of rupiah; null for a missing or zero price. */
export function formatPrice(rupiah: number | null | undefined, prefix = ""): string | null {
  if (!rupiah || !Number.isFinite(rupiah) || rupiah <= 0) return null;
  return `${prefix}Rp ${Math.round(rupiah).toLocaleString("id-ID")}`;
}
